import { Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { NuevoCasoDialog } from "@/components/laboratorio/NuevoCasoDialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useBorrarCaso, useCasos } from "@/hooks/useLaboratorio";
import { getApiErrorMessage } from "@/lib/api";
import { formatearFecha } from "@/lib/fechas";
import { tonoDeLaEtiquetaDelCaso, type CasoDeLaboratorio } from "@/lib/laboratorio";

const CABECERA = "text-ink-medio h-auto px-0 py-[0.5625rem] text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase";

/**
 * Pestaña «Casos de prueba»: las fotos congeladas de informes reales con que se
 * prueban los prompts. Un caso no se edita: se crea, se mira y se borra.
 */
export function CasosDelLaboratorio({ onIrALasCorridas }: { onIrALasCorridas: () => void }) {
  const casos = useCasos();
  const [creando, setCreando] = useState(false);
  const [aBorrar, setABorrar] = useState<CasoDeLaboratorio | null>(null);

  return (
    <Card variant="marcada" className="gap-0 overflow-hidden rounded-[1.25rem] py-0">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
        <p className="text-ink-secundario max-w-[62ch] text-[0.8125rem] font-semibold">
          Un caso es la foto congelada de un informe real: hoja de hechos, documentos y dudas resueltas. No cambia aunque
          el informe siga avanzando.
        </p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" className="rounded-[0.75rem] text-[0.78125rem] font-bold" onClick={onIrALasCorridas}>
            Nuevo caso desde una corrida
          </Button>
          <Button type="button" variant="secondary" className="rounded-[0.75rem] text-[0.78125rem] font-bold" onClick={() => setCreando(true)}>
            Nuevo caso
          </Button>
        </div>
      </div>

      {casos.isPending ? (
        <Skeleton className="m-5 h-40" />
      ) : casos.isError ? (
        <p className="text-meta text-danger-texto px-5 pb-5">No pudimos cargar los casos.</p>
      ) : casos.data.length === 0 ? (
        <p className="text-meta text-ink-medio border-border border-t px-5 py-8 text-center">
          Todavía no hay casos. Congela un informe para probar tus prompts con datos reales.
        </p>
      ) : (
        <Table className="border-border border-t">
          <TableHeader className="bg-superficie-tenue">
            <TableRow>
              <TableHead className={`${CABECERA} pl-5`}>Caso</TableHead>
              <TableHead className={CABECERA}>Tipo</TableHead>
              <TableHead className={CABECERA}>Informe de origen</TableHead>
              <TableHead className={CABECERA}>Hechos</TableHead>
              <TableHead className={CABECERA}>Documentos</TableHead>
              <TableHead className={CABECERA}>Dudas</TableHead>
              <TableHead className={CABECERA}>Texto real</TableHead>
              <TableHead className={`${CABECERA} pr-5`}>
                <span className="sr-only">Acciones</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {casos.data.map((c) => {
              const tono = tonoDeLaEtiquetaDelCaso(c.etiqueta);

              return (
                <TableRow key={c.id} className="border-divisor-fuerte min-h-[3.25rem]">
                  <TableCell className="py-2 pl-5">
                    <div className="text-[0.8125rem] font-bold">{c.nombre}</div>
                    <div className="text-ink-medio font-mono text-[0.65625rem] font-semibold">
                      Congelado {formatearFecha(c.congelado_en)}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    {tono ? (
                      <Etiqueta tono={tono} className="px-2 py-0.5">
                        {c.etiqueta}
                      </Etiqueta>
                    ) : (
                      <span className="text-ink-hint">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-ink-secundario py-2 font-mono text-[0.75rem] font-semibold">{c.informe?.numero ?? "—"}</TableCell>
                  <TableCell className="py-2 font-mono text-[0.78125rem] font-bold">{c.hechos}</TableCell>
                  <TableCell className="py-2 font-mono text-[0.78125rem] font-bold">{c.documentos}</TableCell>
                  <TableCell className="py-2 font-mono text-[0.78125rem] font-bold">{c.dudas}</TableCell>
                  <TableCell className="py-2 text-[0.75rem] font-bold">
                    {c.tiene_texto_real ? (
                      <span className="text-success-soft-foreground">Disponible</span>
                    ) : (
                      <span className="text-ink-hint">Aún no emitido</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 pr-5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Eliminar el caso ${c.nombre}`}
                      onClick={() => setABorrar(c)}
                    >
                      <Trash2Icon aria-hidden />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <NuevoCasoDialog origen={creando ? { corridaId: null } : null} onCerrar={() => setCreando(false)} />
      <ConfirmarBorrado caso={aBorrar} onCerrar={() => setABorrar(null)} />
    </Card>
  );
}

function ConfirmarBorrado({ caso, onCerrar }: { caso: CasoDeLaboratorio | null; onCerrar: () => void }) {
  const borrar = useBorrarCaso();

  return (
    <AlertDialog open={caso !== null} onOpenChange={(abierto) => !abierto && !borrar.isPending && onCerrar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar el caso?</AlertDialogTitle>
          <AlertDialogDescription>
            «{caso?.nombre}» se borra del laboratorio. Los experimentos que ya lo usaron conservan sus resultados.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={borrar.isPending}>Cancelar</AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={borrar.isPending}
            onClick={() =>
              caso &&
              borrar.mutate(caso.id, {
                onSuccess: () => {
                  toast.success("Caso eliminado.");
                  onCerrar();
                },
                onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos eliminar el caso.")),
              })
            }
          >
            Eliminar caso
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
